import React, { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { MaterialReactTable } from "material-react-table";
import { Box } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import TitlePages from "../../../layout/TitlePages";
import Breadcrumbs from "../../../layout/Breadcrumbs";
import Expandible from "../../../layout/HelperDataTable/Expandible";
import { FilterComponent, useCompleteTableConfig } from "../../../constants";
import { dataAcuerdos2026 } from "../../../data/2026/dataAcuerdos";

const baseUrl = "https://itetlax.org.mx/assets/pdf/acuerdos/ITE/2026/";
const sigecStorageUrl = "https://sigec.test/storage/";

// Helper para formatear el identificador del acuerdo / resolución
const formatAcuerdoNumber = (row) => {
  if (row.numDoc) {
    const val = String(row.numDoc).trim();
    if (val.toUpperCase().startsWith("ITE-CG") || val.toUpperCase().startsWith("INE/CG")) {
      return val;
    }
    if (!isNaN(val) && Number(val) < 1000) {
      return `ITE-CG ${val}-2026`;
    }
    return val;
  }
  return `ITE-CG ${row.id}-2026`;
};

// Helper para resolver la URL del PDF (sigec local o itetlax.org.mx remoto)
const resolvePdfUrl = (filePath, fallbackId, isAnnex = false, annexIndex = "") => {
  if (filePath && typeof filePath === "string") {
    // Si ya es una URL absoluta
    if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
      return filePath;
    }
    // Si es un archivo almacenado en SIGEC
    if (filePath.startsWith("acuerdos/") || filePath.startsWith("assets/pdf/acuerdos/2026/")) {
      return `${sigecStorageUrl}${filePath}`;
    }
  }

  // Enlace para acuerdos históricos 1..59 alojados en itetlax.org.mx
  if (isAnnex) {
    return `${baseUrl}${fallbackId}.${annexIndex}.pdf`;
  }
  return `${baseUrl}${fallbackId}.pdf`;
};

// Componente para el enlace al PDF
const PdfLink = ({ url }) => {
  if (!url) return null;
  const fullUrl = url.startsWith("http://") || url.startsWith("https://")
    ? url
    : `${baseUrl}${url}`;

  return (
    <a href={fullUrl} target="_blank" rel="noreferrer">
      <FontAwesomeIcon icon={faFilePdf} className="btn btn-danger" />
    </a>
  );
};

// Fila de la tabla: usa inline style para diferenciar
const TableRow = ({ title, url, isSub }) => (
  <tr>
    <td
      style={{
        fontWeight: isSub ? "normal" : "bold",
        fontStyle: isSub ? "italic" : "normal",
        paddingLeft: isSub ? "20px" : "0",
      }}
    >
      {isSub ? "└ " : ""}
      {title ? title.toUpperCase() : ""}
    </td>
    <td>
      <PdfLink url={url} />
    </td>
  </tr>
);

const AcuerdosITE2026 = () => {
  const [dataAcuerdos, setDataAcuerdos] = useState(dataAcuerdos2026);

  useEffect(() => {
    document.title = `Acuerdos ITE 2026`;

    const loadAcuerdos = async () => {
      const endpoints = [
        "https://sigec.test/api/acuerdos/ite?year=2026",
        "http://sigec.test/api/acuerdos/ite?year=2026",
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint, {
            headers: { Accept: "application/json" },
          });
          if (response.ok) {
            const apiData = await response.json();
            if (Array.isArray(apiData) && apiData.length > 0) {
              setDataAcuerdos(apiData);
              break;
            }
          }
        } catch (e) {
          // Intentar el siguiente endpoint o mantener los datos precargados
        }
      }
    };

    loadAcuerdos();
  }, []);

  const columnsAcuerdos = useMemo(
    () => [
      {
        accessorKey: "monthDoc",
        header: "MES",
        footer: "MES",
        size: 30,
        Filter: FilterComponent,
      },
      {
        accessorFn: (row) => formatAcuerdoNumber(row),
        id: "acuerdo",
        header: "ACUERDO",
        footer: "ACUERDO",
        size: 55,
      },
      {
        accessorFn: (row) =>
          row.nameDoc ? `${row.typeDoc || "ACUERDO"} ${row.nameDoc}` : "",
        id: "titulo",
        header: "TÍTULO",
        footer: "TÍTULO",
        size: 255,
      },
    ],
    []
  );

  const renderDetailPanelAcuerdos = ({ row }) => {
    const anexoTitles = Object.keys(row.original).filter(
      (key) => key.startsWith("titleAnexo") && row.original[key]
    );

    const acuerdoNum = formatAcuerdoNumber(row.original);
    const docType = row.original.typeDoc || "ACUERDO";
    const mainPdfUrl = resolvePdfUrl(row.original.filePath, row.original.id);

    return (
      <Box>
        <div className="table-responsive">
          <table className="table table-hover table-sm table-bordered table align-middle w-40">
            <thead>
              <tr>
                <td colSpan={2}>
                  <br />
                  <strong>{docType.split("").join(" ")}</strong>
                  <br />
                  <br />
                </td>
              </tr>
            </thead>
            <tbody>
              {/* Acuerdo principal */}
              <tr className="table-secondary">
                <td>
                  {(() => {
                    const title = row.original.nameDoc || "";
                    const label = acuerdoNum.toUpperCase().startsWith(docType.toUpperCase())
                      ? acuerdoNum
                      : `${docType} ${acuerdoNum}`;
                    return `${label} ${title}`.trim();
                  })()}
                </td>
                <td>
                  {mainPdfUrl && <PdfLink url={mainPdfUrl} />}
                </td>
              </tr>

              {anexoTitles.length > 0 && (
                <tr>
                  <td colSpan={2}>
                    <br />
                    <strong>A N E X O</strong>
                    <br />
                    <br />
                  </td>
                </tr>
              )}

              {/* Renderizado de anexos y subanexos */}
              {(() => {
                const grouped = {};

                anexoTitles.forEach((key) => {
                  const index = key.replace("titleAnexo", "");
                  const [main, sub] = index.split("_");

                  if (!grouped[main]) grouped[main] = { main: null, subs: [] };

                  if (sub) grouped[main].subs.push({ key, index });
                  else grouped[main].main = { key, index };
                });

                return Object.keys(grouped).map((mainKey) => {
                  const group = grouped[mainKey];
                  return (
                    <React.Fragment key={mainKey}>
                      {group.main && (
                        <TableRow
                          title={row.original[group.main.key]}
                          url={resolvePdfUrl(
                            row.original[`fileAnexo${group.main.index}`] ||
                              row.original[`fileAnexoPDF${group.main.index}`],
                            row.original.id,
                            true,
                            group.main.index
                          )}
                          isSub={false}
                        />
                      )}
                      {group.subs.map((sub) => (
                        <TableRow
                          key={sub.index}
                          title={row.original[sub.key]}
                          url={resolvePdfUrl(
                            row.original[`fileAnexo${sub.index}`] ||
                              row.original[`fileAnexoPDF${sub.index}`],
                            row.original.id,
                            true,
                            sub.index
                          )}
                          isSub={true}
                        />
                      ))}
                    </React.Fragment>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </Box>
    );
  };

  const tableAcuerdos = useCompleteTableConfig(
    dataAcuerdos,
    columnsAcuerdos,
    renderDetailPanelAcuerdos
  );

  return (
    <div className="margin-bottom-2">
      <Breadcrumbs path={[{ label: `Acuerdos 2026` }]} />
      <TitlePages title="Acuerdos ITE" subTitle="Acuerdos ITE 2026" />
      <Expandible />
      <MaterialReactTable table={tableAcuerdos} />
    </div>
  );
};

export default AcuerdosITE2026;

// PropTypes
PdfLink.propTypes = {
  url: PropTypes.string.isRequired,
};

TableRow.propTypes = {
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  isSub: PropTypes.bool,
};